import { type ReactNode, useEffect, useMemo } from 'react';
import './InteractiveTable.css';

/** A column definition for an InteractiveTable. */
export interface InteractiveTableColumn<T> {
  /** Column header text. */
  header: string;
  /** Extracts the cell content for a given row. */
  cell: (row: T) => ReactNode;
  /** Optional per-cell className (e.g. "not-recorded" for missing values). */
  cellClassName?: (row: T) => string | undefined;
  /** Optional alignment override for this column's header + cells. */
  align?: 'left' | 'right' | 'center';
  /**
   * When true, this column can be hidden if ALL rows have empty/missing values.
   * Columns with `hideable: false` or undefined are always shown (e.g. "Client name").
   */
  hideable?: boolean;
  /**
   * Returns true if the cell value for this row is considered "empty" (missing data).
   * Used in conjunction with `hideable` to determine if an entire column should be hidden.
   * If not provided but `hideable` is true, the column is never auto-hidden.
   */
  isEmpty?: (row: T) => boolean;
}

interface InteractiveTableProps<T> {
  /** Column definitions, in display order. */
  columns: InteractiveTableColumn<T>[];
  /** Row data. */
  rows: T[];
  /** Stable key for each row. */
  rowKey: (row: T) => string;
  /** Called with the chosen row when a row is selected (click / Enter / Space). */
  onSelect: (row: T) => void;
  /** Accessible label for a row's select action. */
  rowAriaLabel: (row: T) => string;
  /** Optional extra class on the outer wrapper. */
  className?: string;
  /**
   * Key of the resolved (chosen) row, or null/undefined when the table is
   * unresolved. When set, the table enters its RESOLVED state: the matching row
   * is emphasised (bold, `aria-selected`) and ALL rows become non-selectable
   * (the chosen one is already committed; the rest are no longer actionable).
   * This is a presentational lock only — the caller's post-selection behaviour
   * is unchanged.
   */
  selectedRowKey?: string | null;
  /**
   * Called when columns are hidden due to all rows having empty values.
   * Receives the list of hidden column headers (e.g. ["Location", "Adviser"]).
   * The parent can use this to render an informational message.
   */
  onColumnsHidden?: (hiddenColumnHeaders: string[]) => void;
}

/**
 * Filters out hideable columns where ALL rows have empty values.
 * Returns the visible columns and the list of hidden column headers.
 */
function filterEmptyColumns<T>(
  columns: InteractiveTableColumn<T>[],
  rows: T[],
): { visibleColumns: InteractiveTableColumn<T>[]; hiddenHeaders: string[] } {
  const hiddenHeaders: string[] = [];
  const visibleColumns = columns.filter((col) => {
    // Non-hideable columns are always shown
    if (!col.hideable || !col.isEmpty) {
      return true;
    }
    // Check if ALL rows have empty values for this column
    const allEmpty = rows.length > 0 && rows.every((row) => col.isEmpty!(row));
    if (allEmpty) {
      hiddenHeaders.push(col.header);
      return false;
    }
    return true;
  });
  return { visibleColumns, hiddenHeaders };
}

/**
 * A generic selectable ("interactive") data table.
 *
 * Visual styling: Base table styles (header color, rounded border container,
 * "not recorded" italic) are inherited from .porter-response in
 * ChatResponse.css. InteractiveTable.css adds the plain (non-zebra) rows plus
 * the interactive row behavior (cursor, hover highlight, focus ring) that is
 * common to ALL interactive tables — not just disambiguation.
 *
 * Each row is keyboard-focusable and selectable; selecting a row hands the
 * row's data back to the caller via `onSelect`.
 *
 * Resolved state: when `selectedRowKey` is set, the table is locked — the
 * chosen row is emphasised and announced as selected, and no row is selectable
 * (disabled rows are removed from the tab order and marked `aria-disabled`).
 */
export function InteractiveTable<T>({
  columns,
  rows,
  rowKey,
  onSelect,
  rowAriaLabel,
  className,
  selectedRowKey,
  onColumnsHidden,
}: Readonly<InteractiveTableProps<T>>) {
  // Filter out hideable columns where ALL rows have empty values
  const { visibleColumns, hiddenHeaders } = useMemo(
    () => filterEmptyColumns(columns, rows),
    [columns, rows],
  );

  // Notify parent of hidden columns (for informational message)
  useEffect(() => {
    if (onColumnsHidden) {
      onColumnsHidden(hiddenHeaders);
    }
  }, [hiddenHeaders, onColumnsHidden]);

  const isResolved = selectedRowKey != null;
  return (
    <div className={['porter-interactive-table', 'porter-response', className].filter(Boolean).join(' ')}>
      <div className="table-scroll-container">
        <table>
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.header}
                  scope="col"
                  style={col.align ? { textAlign: col.align } : undefined}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const isSelected = selectedRowKey === key;
              const rowClassName = [
                'porter-interactive-table__row',
                isResolved ? 'porter-interactive-table__row--locked' : '',
                isSelected ? 'porter-interactive-table__row--selected' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <tr
                  key={key}
                  className={rowClassName}
                  aria-selected={isResolved ? isSelected : undefined}
                  aria-disabled={isResolved && !isSelected ? true : undefined}
                >
                  {visibleColumns.map((col, colIndex) => (
                    <td
                      key={col.header}
                      className={col.cellClassName?.(row) || undefined}
                      style={col.align ? { textAlign: col.align } : undefined}
                    >
                      {colIndex === 0 && !isResolved ? (
                        <button
                          type="button"
                          className="porter-interactive-table__row-button"
                          onClick={() => onSelect(row)}
                          aria-label={rowAriaLabel(row)}
                        >
                          {col.cell(row)}
                        </button>
                      ) : (
                        col.cell(row)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
